import {Alert, Card, Col, Row, Spinner} from 'react-bootstrap';
import { PageHeader } from '../components/PageHeader';
import { useParams, Link } from "react-router-dom";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import { fetchReportData, fetchCurrentReportStatus } from "../api/reportApi";
import { ReportForm } from "../components/ReportForm";
import { LastReports } from "../components/LastReports";
import { useEffect, useRef } from 'react';

export function ReportPage() {
  const { report } = useParams();
  const lastRunningReportId = useRef(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
      queryKey: ['report-data', report],
      queryFn: fetchReportData,
      enabled: !!report,
      staleTime: 1000 * 3600,
  });

  const { data: currentStatus, isLoading: isStatusLoading } = useQuery({
    queryKey: ['report-current-status'],
    queryFn: fetchCurrentReportStatus,
    refetchInterval: (query) => {
      return query.state.data ? 500 : false;
    },
  });

  useEffect(() => {
    if (currentStatus && currentStatus.report_slug === report) {
      lastRunningReportId.current = currentStatus.id;
    } else if (!currentStatus && lastRunningReportId.current) {
      queryClient.invalidateQueries({ queryKey: ['last-reports'] });
      // Report finished, trigger download
      const downloadUrl = `/api/reports/runs/${lastRunningReportId.current}/download`;
      window.location.href = downloadUrl;
      lastRunningReportId.current = null;
    }
  }, [currentStatus, report]);

  const isCurrentReportRunning = !!currentStatus;
  const isDifferentReportRunning = isCurrentReportRunning && currentStatus.report_slug !== report;

  const renderReportParameters = () => {
    if (isStatusLoading) {
      return (
        <div className="text-center p-3">
          <Spinner animation="border" size="sm" />
        </div>
      );
    }

    if (isCurrentReportRunning) {
      if (isDifferentReportRunning) {
        return (
          <Alert variant="info">
            Another report (<strong>{currentStatus.name}</strong>) is currently being generated.
            <br />
            <Link to={`/reports/${currentStatus.report_slug}`} className="alert-link">
              Go to currently generated report
            </Link>
          </Alert>
        );
      }

      return (
        <Alert variant="info">
          <Spinner animation="border" size="sm" className="me-2" />
          <strong>{currentStatus.status}</strong>
          <div className="mt-2 small text-muted">
            Started: {new Date(currentStatus.created).toLocaleString()}
          </div>
        </Alert>
      );
    }

    return (
      <>
        {data?.can_create_report && <ReportForm data={data} isError={isError} error={error} />}
        {(data && !data?.can_create_report) && <Alert variant='danger'>{data?.can_create_report_message}</Alert>}
      </>
    );
  };

  return (
    <div className="page-container">
      <PageHeader title={data?.name || "Loading..."}/>
      <Row className="g-3">
        <Col lg={5}>
          <Card className="shadow-sm">
            <Card.Header>Report parameters</Card.Header>
            <Card.Body>
                {renderReportParameters()}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={7}>
          <Card className="shadow-sm">
            <Card.Header>Last reports</Card.Header>
            <Card.Body className="p-0">
              <LastReports report={report} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
